import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import moment, { Moment } from "moment";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGridPro, GridColDef, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { ConfirmationDialog } from "../../pricing-management/pages/deals-page/delete-dilog.tsx";
import AdminAPI, { Organization, OrganizationType, ORGANIZATION_TYPES } from "../api/admin-api.ts";

function OrganizationsPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackMessage, setSnackMessage] = useState<string | undefined>(undefined);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Organization | null>(null);
  const [name, setName] = useState("");
  const [expiration, setExpiration] = useState<Moment | null>(null);
  const [hasNationalPrivatePlacement, setHasNationalPrivatePlacement] = useState(false);
  const [type, setType] = useState<OrganizationType>("Bank");

  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    const token = await getAccessTokenSilently();
    const result = await AdminAPI.getOrganizations(token);
    setLoading(false);
    if (!result.valid) return setSnackMessage(result.error);
    setOrganizations(result.body);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const openCreateDialog = () => {
    setEditing(null);
    setName("");
    setExpiration(moment().add(1, "year"));
    setHasNationalPrivatePlacement(true);
    setType("Bank");
    setDialogOpen(true);
  };

  const openEditDialog = (organization: Organization) => {
    setEditing(organization);
    setName(organization.name);
    setExpiration(moment(new Date(organization.expiration)));
    setHasNationalPrivatePlacement(organization.has_national_private_placement);
    setType(organization.type);
    setDialogOpen(true);
  };

  const canSave = name.trim().length > 0 && expiration !== null && expiration.isValid();

  const handleSave = async () => {
    if (!canSave) return;
    const payload = {
      name: name.trim(),
      expiration: expiration!.toISOString(),
      has_national_private_placement: hasNationalPrivatePlacement,
      type,
    };
    const token = await getAccessTokenSilently();
    const result = editing
      ? await AdminAPI.updateOrganization(token, editing.id, payload)
      : await AdminAPI.createOrganization(token, payload);
    if (!result.valid) return setSnackMessage(result.error);
    setDialogOpen(false);
    setSnackMessage(editing ? "Organization updated." : "Organization created.");
    fetchOrganizations();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const token = await getAccessTokenSilently();
    const result = await AdminAPI.deleteOrganization(token, deleteTarget.id);
    setDeleteTarget(null);
    if (!result.valid) return setSnackMessage(result.error);
    setSnackMessage("Organization deleted.");
    fetchOrganizations();
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "type", headerName: "Type", width: 120 },
    {
      field: "expiration",
      headerName: "Expiration",
      width: 120,
      valueFormatter: (value) => moment(new Date(value as string)).format("MM/DD/YYYY"),
    },
    {
      field: "has_national_private_placement",
      headerName: "National Private Placement",
      width: 180,
      valueFormatter: (value) => (value ? "Yes" : "No"),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon fontSize="small" />}
          label="Edit"
          onClick={() => openEditDialog(params.row as Organization)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon fontSize="small" />}
          label="Delete"
          onClick={() => setDeleteTarget(params.row as Organization)}
        />,
      ],
    },
  ];

  return (
    <Stack gap={2} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ color: "white" }}
        >
          Add Organization
        </Button>
      </Stack>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <DataGridPro
          rows={organizations}
          columns={columns}
          filterMode="client"
          disableMultipleColumnsSorting={false}
          disableMultipleColumnsFiltering={false}
          sx={{ border: 0 }}
          loading={loading}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editing ? "Edit Organization" : "Add Organization"}</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value as OrganizationType)}
              size="small"
              select
              fullWidth
            >
              {ORGANIZATION_TYPES.map((organizationType) => (
                <MenuItem key={organizationType} value={organizationType}>{organizationType}</MenuItem>
              ))}
            </TextField>
            <DatePicker
              label="Expiration"
              value={expiration}
              onChange={(value) => setExpiration(value)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasNationalPrivatePlacement}
                  onChange={(e) => setHasNationalPrivatePlacement(e.target.checked)}
                />
              }
              label="National Private Placement"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ color: "white" }} disabled={!canSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        title="Delete Organization"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onAccept={handleDelete}
      />

      <Snackbar
        open={snackMessage !== undefined}
        autoHideDuration={6000}
        onClose={() => setSnackMessage(undefined)}
        message={snackMessage}
      />
    </Stack>
  );
}

export default OrganizationsPage;
